import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import * as faceService from '@/api/services/face.service';
import type {
  PersonFilters,
  PersonMergePayload,
  PersonUpdatePayload,
  WizardDecisionPayload,
} from '@/api/types/face.types';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const FACE_KEYS = {
  all: ['faces'] as const,
  persons: () => [...FACE_KEYS.all, 'persons'] as const,
  personList: (filters?: PersonFilters) =>
    [...FACE_KEYS.persons(), 'list', filters] as const,
  personDetail: (id: string) =>
    [...FACE_KEYS.persons(), 'detail', id] as const,
  personPhotos: (id: string) =>
    [...FACE_KEYS.persons(), 'photos', id] as const,
  wizard: () => [...FACE_KEYS.all, 'wizard'] as const,
  wizardStatus: (eventId: string) =>
    [...FACE_KEYS.wizard(), 'status', eventId] as const,
  wizardClusters: (eventId: string) =>
    [...FACE_KEYS.wizard(), 'clusters', eventId] as const,
  wizardAmbiguous: (eventId: string) =>
    [...FACE_KEYS.wizard(), 'ambiguous', eventId] as const,
  wizardUnassigned: (eventId: string) =>
    [...FACE_KEYS.wizard(), 'unassigned', eventId] as const,
};

// ---------------------------------------------------------------------------
// Person hooks
// ---------------------------------------------------------------------------

/** Fetch paginated list of persons. */
export function usePersons(filters?: PersonFilters) {
  return useQuery({
    queryKey: FACE_KEYS.personList(filters),
    queryFn: () => faceService.getPersons(filters),
    enabled: !!filters?.event,
  });
}

/** Fetch a single person detail with face gallery. */
export function usePerson(id: string) {
  return useQuery({
    queryKey: FACE_KEYS.personDetail(id),
    queryFn: () => faceService.getPerson(id),
    enabled: !!id,
  });
}

/** Fetch photos for a person. */
export function usePersonPhotos(
  personId: string,
  params?: { page?: number; page_size?: number },
) {
  return useQuery({
    queryKey: [...FACE_KEYS.personPhotos(personId), params],
    queryFn: () => faceService.getPersonPhotos(personId, params),
    enabled: !!personId,
  });
}

/** Update a person (name / role). */
export function useUpdatePerson() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PersonUpdatePayload }) =>
      faceService.updatePerson(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      queryClient.invalidateQueries({
        queryKey: FACE_KEYS.personDetail(variables.id),
      });
      toast.success(t('faces.personUpdated'));
    },
    onError: () => {
      toast.error(t('faces.personUpdateError'));
    },
  });
}

/** Delete (deactivate) a person. */
export function useDeletePerson() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => faceService.deletePerson(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      toast.success(t('faces.personDeleted'));
    },
    onError: () => {
      toast.error(t('faces.personDeleteError'));
    },
  });
}

/** Merge two persons. */
export function useMergePersons() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: PersonMergePayload) =>
      faceService.mergePersons(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.wizard() });
      toast.success(t('faces.personsMerged'));
    },
    onError: () => {
      toast.error(t('faces.mergeError'));
    },
  });
}

// ---------------------------------------------------------------------------
// Wizard hooks
// ---------------------------------------------------------------------------

/** Fetch wizard status for an event. */
export function useWizardStatus(eventId: string) {
  return useQuery({
    queryKey: FACE_KEYS.wizardStatus(eventId),
    queryFn: () => faceService.getWizardStatus(eventId),
    enabled: !!eventId,
    refetchInterval: (query) => {
      // Poll while clustering is in progress
      const data = query.state.data;
      if (data && !data.is_clustered) return 3000;
      return false;
    },
  });
}

/** Start face clustering for an event. */
export function useStartClustering() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (eventId: string) => faceService.startClustering(eventId),
    onSuccess: (_data, eventId) => {
      queryClient.invalidateQueries({
        queryKey: FACE_KEYS.wizardStatus(eventId),
      });
      toast.success(t('faces.clusteringStarted'));
    },
    onError: () => {
      toast.error(t('faces.clusteringError'));
    },
  });
}

/** Fetch wizard clusters for an event. */
export function useWizardClusters(eventId: string) {
  return useQuery({
    queryKey: FACE_KEYS.wizardClusters(eventId),
    queryFn: () => faceService.getWizardClusters(eventId),
    enabled: !!eventId,
  });
}

/** Fetch ambiguous pairs for the wizard. */
export function useAmbiguousPairs(eventId: string, limit?: number) {
  return useQuery({
    queryKey: FACE_KEYS.wizardAmbiguous(eventId),
    queryFn: () => faceService.getAmbiguousPairs(eventId, limit),
    enabled: !!eventId,
  });
}

/** Submit wizard decisions. */
export function useSubmitDecisions() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      eventId,
      payload,
    }: {
      eventId: string;
      payload: WizardDecisionPayload;
    }) => faceService.submitDecisions(eventId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: FACE_KEYS.wizardAmbiguous(variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: FACE_KEYS.wizardStatus(variables.eventId),
      });
      toast.success(t('faces.decisionsSubmitted'));
    },
    onError: () => {
      toast.error(t('faces.decisionsError'));
    },
  });
}

/** Trigger re-clustering with decisions. */
export function useRecluster() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (eventId: string) => faceService.recluster(eventId),
    onSuccess: (_data, _eventId) => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.wizard() });
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      toast.success(t('faces.reclusterStarted'));
    },
    onError: () => {
      toast.error(t('faces.reclusterError'));
    },
  });
}

/** Fetch unassigned faces for an event. */
export function useUnassignedFaces(eventId: string) {
  return useQuery({
    queryKey: FACE_KEYS.wizardUnassigned(eventId),
    queryFn: () => faceService.getUnassignedFaces(eventId),
    enabled: !!eventId,
  });
}

/** Remove faces from a person. */
export function useRemoveFaces() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ personId, faceIds }: { personId: string; faceIds: string[] }) =>
      faceService.removeFaces(personId, faceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.wizard() });
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      toast.success(t('faces.facesRemoved'));
    },
    onError: () => {
      toast.error(t('faces.facesRemoveError'));
    },
  });
}

/** Assign faces to a person. */
export function useAssignFaces() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ personId, faceIds }: { personId: string; faceIds: string[] }) =>
      faceService.assignFaces(personId, faceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.wizard() });
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      toast.success(t('faces.facesAssigned'));
    },
    onError: () => {
      toast.error(t('faces.facesAssignError'));
    },
  });
}


/** Split a person cluster by a seed face. */
export function useSplitPerson() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('faces');

  return useMutation({
    mutationFn: ({ personId, faceId }: { personId: string; faceId: string }) =>
      faceService.splitPerson(personId, faceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.wizard() });
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      toast.success(t('splitSuccess'));
    },
    onError: () => {
      toast.error(t('splitError'));
    },
  });
}

/** Dismiss (delete) an entire person cluster. */
export function useDismissCluster() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('faces');

  return useMutation({
    mutationFn: (personId: string) => faceService.deletePerson(personId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.wizard() });
      queryClient.invalidateQueries({ queryKey: FACE_KEYS.persons() });
      toast.success(t('clusterDismissed'));
    },
    onError: () => {
      toast.error(t('clusterDismissError'));
    },
  });
}
